import { forwardRef } from 'react';
import { HiMagnifyingGlass, HiXMark } from 'react-icons/hi2';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  isLoading?: boolean;
}

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  function SearchBar(
    { value, onChange, onSubmit, placeholder = 'Search...', isLoading = false },
    ref
  ) {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onSubmit();
      }
    };

    const handleClear = () => {
      onChange('');
    };

    return (
      <div className="relative w-full">
        {/* Search icon on left */}
        <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-[var(--color-border)]
                          border-t-[var(--color-primary)] rounded-full animate-spin" />
          ) : (
            <HiMagnifyingGlass
              size={16}
              className="text-[var(--color-text-muted)]"
            />
          )}
        </div>

        <input
          ref={ref}
          type="search"
          role="searchbox"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          className="w-full pl-8 pr-8 py-1.5 text-sm
                     text-[var(--color-text)] bg-[var(--color-surface-elevated)]
                     border border-[var(--color-border)]
                     rounded-full
                     focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/50
                     focus:border-[var(--color-primary)]
                     placeholder:text-[var(--color-text-muted)]
                     transition-all duration-150
                     [&::-webkit-search-cancel-button]:hidden"
        />

        {/* Clear button on right */}
        {value && (
          <button
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1
                       text-[var(--color-text-muted)]
                       hover:text-[var(--color-text)]
                       transition-colors rounded-full
                       focus:outline-none focus-visible:ring-2
                       focus-visible:ring-[var(--color-primary)]"
          >
            <HiXMark size={14} />
          </button>
        )}
      </div>
    );
  }
);
